import React, { useState, useEffect, Suspense } from 'react';
import { 
  Box, Typography, IconButton, Button, CircularProgress, 
  Paper, Tabs, Tab
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import Moment from 'moment';
import { numeral as Numeral } from '../utils';
import { alpha } from '@mui/material/styles'; 
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

import SecureImage from './SecureImage';
import { resolveFileUrl } from './SecureFileDisplay';
import { formatDisplayValue } from "./DataListItems";
import DynamicCustomView from "./DynamicCustomView";
import RecordLookupMenu from './RecordLookupMenu';
import ObjectArrayControl from './ObjectArrayControl';
import ViewValue, { summarizeValue } from './ViewValue';
import { splitVoucherFields } from './voucherFormTabs';
import { detailTabFields } from './masterDetailSplit';
import {
  normalizeViewTabs, viewTabListProps, evalViewTabContent, isEmptyViewValue, viewValueShape,
} from './viewTabsRules';
import { NEN_DANH_SACH } from './giaoDienDanhSach';
import {
  TheXem, DaiNhanDang, DongNgayTao, OSoLieu, HangSoLieu, OThongTin, LuoiThongTin, laTruongMa,
  MAU_O_SO, SO_O_SO_TOI_DA, sxThanhTabXem, sxNoiDungTab,
} from './TrangXemHienDai';

// Các tab đặc biệt của trang xem — nạp TRỄ: phần lớn bản ghi không khai tab nào, và DataListEmbed kéo
// theo DataListManager (import thẳng là vòng lặp DataListManager -> DataView -> DataListManager).
const DataListEmbed = React.lazy(() => import('./DataListEmbed'));
const Files = React.lazy(() => import('./Files'));
const Notes = React.lazy(() => import('./Notes'));
const ChatRoom = React.lazy(() => import('./ChatRoom'));
const Comment = React.lazy(() => import('./Comment'));

const NOOP = () => {};
// Tiêu đề/phụ đề là object (vd `{ten, ma}`) thì React ném lỗi "Objects are not valid as a React child".
const asText = (v) => (v && typeof v === 'object' ? summarizeValue(v) : v);
const NO_ERRORS = {};
const TAB_DEPS = { moment: Moment, numeral: Numeral };

/** Nội dung MỘT tab của trang xem. */
function ViewTabContent({ tab, item, fields, context, apiCode, moduleCode, isReadOnlyFiles, renderFieldGrid }) {
  const history = useHistory();
  const id = item._id || item.id;
  const fallback = <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>;

  if (tab.kind === 'detail') {
    const rows = item[tab.field.field];
    if (!Array.isArray(rows) || rows.length === 0) {
      return <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Chưa có dòng nào.</Typography>;
    }
    // Có khai cột thì dùng đúng lưới chi tiết của form (chỉ đọc) — định dạng/canh lề/tổng như lúc nhập.
    // Không khai cột (cấu hình thiếu subFields) thì tự suy cột từ dữ liệu thay vì vẽ lưới rỗng.
    if (Array.isArray(tab.field.subFields) && tab.field.subFields.length) {
      return (
        <ObjectArrayControl
          key={`${tab.field.field}_${id || ''}`}
          field={tab.field} value={rows} onChange={NOOP} formErrors={NO_ERRORS}
          isReadOnly masterData={item}
        />
      );
    }
    return <ViewValue value={rows} field={tab.field} row={item} formatCell={(v, f, r) => formatDisplayValue(v, f, r, context, false)} />;
  }
  if (tab.kind === 'fields') return renderFieldGrid(tab.fields, true);

  const t = tab.viewTab;
  switch (t.kind) {
    case 'file':
    case 'files':
      return <Suspense fallback={fallback}><Files idLink={id} readOnly={isReadOnlyFiles} typeView={t.kind === 'files' ? 'list' : undefined} history={history} /></Suspense>;
    case 'note':
      return <Suspense fallback={fallback}><Notes idLink={id} history={history} /></Suspense>;
    case 'chatroom': {
      const titleField = fields.find(f => f.isTitle);
      const attends = [...new Set([...(item.attends || []), item.user_created].filter(Boolean))];
      return (
        <Suspense fallback={fallback}>
          <ChatRoom idLink={id} codeLink={moduleCode} attends={attends} phuTrach={item.phu_trach}
            title={titleField ? item[titleField.field] : (t.title || '')} history={history} />
        </Suspense>
      );
    }
    case 'comment':
      return <Suspense fallback={fallback}><Comment id_product={id} colection_name_product={apiCode} title={t.title || 'Ý kiến'} history={history} /></Suspense>;
    case 'content': {
      const html = evalViewTabContent(t.content_data, item, TAB_DEPS);
      return <Typography component="div" sx={{ px: 1.5 }} dangerouslySetInnerHTML={{ __html: html || '--' }} />;
    }
    case 'task':
    case 'list':
    default: {
      const { condition, defaultValues } = viewTabListProps(t, item, TAB_DEPS);
      return (
        <Suspense fallback={fallback}>
          <DataListEmbed code={t.code} title={t.title} condition={condition} defaultValues={defaultValues} />
        </Suspense>
      );
    }
  }
}

export default function DataView({ 
  item, fields, context, onBack, onEdit, onCopy, onDelete, 
  canEdit = false, canDelete = false, canCopy = false, loading = false,
  customViewScript,
  apiCode, changeCodeForm, onRecordChanged,  // cho MENU TRA CỨU (sổ sách, công nợ, lịch sử, đổi mã…)
  // CÁC TAB của trang xem — tương đương `view_tabs` của listinfo (xem viewTabsRules.js).
  viewTabs, moduleCode, readOnlyFiles,
  renderRowActions,   // (item, 'view') => nút tuỳ chỉnh của bản ghi (rowActions) — xem DataRowActions.jsx
}) {
  const [tabIndex, setTabIndex] = useState(0);
  
  const [coverUrl, setCoverUrl] = useState(null);
  const [isResolvingCover, setIsResolvingCover] = useState(true);
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');

  useEffect(() => {
    let isMounted = true;

    const fetchCoverImage = async () => {
      if (!item) return;
      setIsResolvingCover(true);

      let explicitImage = item.picture || item.hinh_anh || item.cover;
      if (explicitImage) {
        if (isMounted) { setCoverUrl(resolveFileUrl(explicitImage, false, context)); setIsResolvingCover(false); }
        return;
      }

      let fileIdsToCheck = [];
      let firstFallbackImage = null;

      for (const f of fields) {
        if (f.type === 'File' || f.type === 'FileArray' || f.type === 'image') {
          const val = item[f.field];
          if (!val) continue;

          const fileArray = Array.isArray(val) ? val : [val];
          for (const fileItem of fileArray) {
            if (!fileItem) continue;
            
            if (typeof fileItem === 'object') {
              if (fileItem.is_avatar === true || fileItem.is_avatar === 1) {
                if (isMounted) { setCoverUrl(resolveFileUrl(fileItem, false, context)); setIsResolvingCover(false); }
                return;
              }
              if (!firstFallbackImage) firstFallbackImage = fileItem;
            } 
            else if (typeof fileItem === 'string') {
              fileIdsToCheck.push(fileItem);
              if (!firstFallbackImage) firstFallbackImage = fileItem;
            }
          }
        }
      }

      if (fileIdsToCheck.length > 0) {
        try {
          const res = await context.apis.asyncGetList(userToken, 'file', { condition: { _id: { $in: fileIdsToCheck } }, limit: 100 });
          const fetchedFiles = res?.data || res || [];

          const avatarFile = fetchedFiles.find(f => f.is_avatar === true || f.is_avatar === 1);
          if (avatarFile) {
            if (isMounted) { setCoverUrl(resolveFileUrl(avatarFile, false, context)); setIsResolvingCover(false); }
            return;
          }

          const firstImage = fetchedFiles.find(f => {
            const fileName = String(f.name || f.file_name || f.originalname || '');
            return /\.(jpe?g|png|gif|webp)$/i.test(fileName);
          });
          if (firstImage) {
            if (isMounted) { setCoverUrl(resolveFileUrl(firstImage, false, context)); setIsResolvingCover(false); }
            return;
          }

        } catch (error) { console.warn("Lỗi tải thông tin file cover:", error); }
      }

      if (isMounted) {
        setCoverUrl(firstFallbackImage ? resolveFileUrl(firstFallbackImage, false, context) : null);
        setIsResolvingCover(false);
      }
    };

    fetchCoverImage();

    return () => { isMounted = false; };
  }, [item, fields, context, userToken]);

  // ================= 2. HÀM ĐIỀU HƯỚNG ACTION (ĐÓNG VIEW TRƯỚC KHI MỞ FORM) =================
  const handleEditAction = () => {
    onBack(); 
    setTimeout(() => { onEdit(item); }, 150); 
  };

  const handleCopyAction = () => {
    onBack(); 
    setTimeout(() => { onCopy(item); }, 150); 
  };

  const handleDeleteAction = () => {
    onBack(); 
    setTimeout(() => { onDelete(item._id); }, 150);
  };
  // ==============================================================================

  if (loading || !item || isResolvingCover) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
        <CircularProgress color="primary" />
        <Typography color="text.secondary">Đang xử lý dữ liệu...</Typography>
      </Box>
    );
  }

  // ================= BỘ LỌC AN TOÀN CHỐNG SCRIPT RỖNG =================
  const compactScript = customViewScript ? customViewScript.replace(/\s+/g, '') : '';
  const isFakeScript = compactScript === '' || compactScript.includes('<MUI.Box></MUI.Box>');
  const shouldUseCustomView = customViewScript && !isFakeScript;

  // HÀM HỖ TRỢ AI RENDER FIELD (Vũ khí bí mật)
  const renderFieldOutput = (fieldName) => {
      const fieldDef = fields.find(f => f.field === fieldName);
      if (!fieldDef) return <Typography color="error">[{fieldName} not found]</Typography>;
      const val = item[fieldName];
      return formatDisplayValue(val, fieldDef, item, context, true);
  };

  // NÚT TUỲ CHỈNH của bản ghi. Giao diện tuỳ biến tự đặt chỗ bằng biến `rowActions`; script chưa dùng biến đó thì vẫn hiện nút
  // ở thanh trên cùng — thêm nút vào cấu hình không được phụ thuộc vào việc ai đó nhớ sửa giao diện tuỳ biến.
  const nutDong = renderRowActions ? renderRowActions(item, 'view') : null;

  // NẾU CÓ SCRIPT -> RENDER BẰNG CUSTOM VIEW
  if (shouldUseCustomView) {
      const scriptCoChoNut = /\browActions\b/.test(customViewScript);
      return (
          <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: 'background.default',p:1 }}>
              {nutDong && !scriptCoChoNut ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', mb: 1 }}>{nutDong}</Box>
              ) : null}
              <DynamicCustomView
                  script={customViewScript}
                  rowActions={nutDong}
                  item={item}
                  onBack={onBack}
                  onEdit={canEdit ? handleEditAction : null}
                  onCopy={canCopy ? handleCopyAction : null}
                  onDelete={canDelete ? handleDeleteAction : null}
                  formatField={renderFieldOutput}
                  context={context}
              />
          </Box>
      );
  }
  // NẾU KHÔNG -> RENDER VIEW MẶC ĐỊNH
  const statusColor = item.color || null;
  const statusLabel = item.ten_trang_thai || null;

  const evaluateCondition = (condition) => typeof condition === 'function' ? condition(context, item, item, fields) : !!condition;
  
  const excludedFields = ['ten_trang_thai', 'color', 'picture', 'hinh_anh'];
  const titleField = fields.find(f => f.isTitle === true) || fields.find(f => f.type === 'String' && !excludedFields.includes(f.field)) || fields[0];
  const subTitleField = fields.find(f => f.isSubTitle === true);

  const displayFields = fields.filter(f => {
    if (f.field === titleField?.field || f.field === subTitleField?.field || excludedFields.includes(f.field)) return false;
    if (evaluateCondition(f.hideInView)) return false; 
    return true;
  });

  // ================= CHIA PHẦN GIỐNG BỐ CỤC CHỨNG TỪ =================
  // Thông tin chính (không tab / "master" / tổng cộng) ở trên; phía dưới là THANH TAB:
  //   1) mỗi lưới chi tiết (ObjectArray) một tab — KHÔNG xét hideInView: chứng từ cũ khai `on_view:false`
  //      cho field mảng, lọc theo nó là mất sạch chi tiết (cùng luật với RowDetailTabs); chỉ bỏ lưới con
  //      nội bộ (ẩn ở cả danh sách lẫn form);
  //   2) mỗi `formTab` khác một tab field;
  //   3) các tab `viewTabs` (← view_tabs của listinfo): tệp, ghi chú, thảo luận, danh sách liên quan…
  const { masterFields, summaryFields, extraTabs } = splitVoucherFields(displayFields.filter(f => f.type !== 'ObjectArray'));
  const mainFields = [...masterFields, ...summaryFields];
  const tabs = [
    ...detailTabFields(fields).map(f => ({ key: 'detail_' + f.field, title: f.label || f.field, kind: 'detail', field: f })),
    ...extraTabs.map(t => ({ key: 'fields_' + t.title, title: t.title, kind: 'fields', fields: t.fields }))
      .filter(t => t.fields.some(f => !isEmptyViewValue(item[f.field]))),
    ...normalizeViewTabs(viewTabs).map(t => ({ key: t.key, title: t.title, kind: 'view', viewTab: t })),
  ];
  const activeTabIndex = Math.min(tabIndex, Math.max(tabs.length - 1, 0));

  // ================= BỐ CỤC HIỆN ĐẠI (22/10/2026) =================
  // CÙNG bố cục với trang xem ViewBase của bộ cũ — theo trang xem quảng cáo `ad_campaign` của gói marketing:
  // dải nhận dạng → ô SỐ LIỆU CHÍNH (field Number) → "Hình ảnh" → "Thông tin" → thẻ có thanh TAB.
  // Các khối dựng lấy từ TrangXemHienDai.jsx (dùng chung với ViewBase).
  const laSo = (f) => f.type === 'Number' && !isEmptyViewValue(item[f.field]) && viewValueShape(item[f.field], f) === 'scalar';
  const oSo = mainFields.filter(laSo).slice(0, SO_O_SO_TOI_DA);
  const thongTin = mainFields.filter(f => oSo.indexOf(f) < 0);
  const coThongTin = thongTin.some(f => !isEmptyViewValue(item[f.field]));
  const truongMa = fields.find(f => f.field !== titleField?.field && laTruongMa(f.field) && !isEmptyViewValue(item[f.field]) && typeof item[f.field] !== 'object');
  const tieuDe = titleField && !isEmptyViewValue(item[titleField.field]) ? asText(item[titleField.field]) : 'Chi tiết bản ghi';
  const dinhDangNgay = (d) => Moment(d).format('DD/MM/YYYY HH:mm');
  const chips = [];
  if (statusLabel) chips.push({ label: statusLabel, sx: { fontWeight: 'bold', bgcolor: statusColor || 'text.secondary', color: '#fff' } });
  if (truongMa) chips.push({ label: (truongMa.label || truongMa.field) + ': ' + asText(item[truongMa.field]), variant: 'outlined' });

  const renderFieldGrid = (list, inTab) => {
    const shown = list.filter(f => !isEmptyViewValue(item[f.field]));
    if (!shown.length) {
      return inTab ? <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>Không có dữ liệu.</Typography> : null;
    }
    return (
      <LuoiThongTin>
        {shown.map(f => {
          const val = item[f.field];
          // Field PHỨC (object / mảng object) luôn chiếm trọn hàng — bảng hay danh sách nhãn/giá trị bị
          // nhét vào nửa cột là không đọc được.
          const shape = viewValueShape(val, f);
          const isFullWidth = f.type === 'HTML' || f.type === 'RichText' || f.type === 'FileArray' || f.multiline || shape === 'detail' || shape === 'object';
          return (
            <OThongTin key={f.field} nhan={f.label} rong={isFullWidth} size={f.gridView}>
              {formatDisplayValue(val, f, item, context, true)}
            </OThongTin>
          );
        })}
      </LuoiThongTin>
    );
  };

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: NEN_DANH_SACH }}>

      <Paper elevation={0} sx={{ position: 'sticky', top: 0, zIndex: 10, borderRadius: 0, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, pb: 1,pt: 'max(12px, env(safe-area-inset-top))', bgcolor: 'background.paper' }}>
        <Button startIcon={<ArrowBackIosNewIcon />} onClick={onBack} sx={{ textTransform: 'none', color: 'primary.main', fontWeight: 600, fontSize: '1rem' }}>
          Quay lại
        </Button>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {nutDong}

          {/* NÚT COPY */}
          {canCopy && (
            <IconButton 
              onClick={handleCopyAction}
              sx={{ color: 'success.main', bgcolor: (theme) => alpha(theme.palette.success.main, 0.1), '&:hover': { bgcolor: (theme) => alpha(theme.palette.success.main, 0.2) } }}
            >
              <ContentCopyOutlinedIcon />
            </IconButton>
          )}

          {canEdit && (
            <IconButton 
              onClick={handleEditAction}
              sx={{ color: 'warning.main', bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1), '&:hover': { bgcolor: (theme) => alpha(theme.palette.warning.main, 0.2) } }}
            >
              <EditIcon />
            </IconButton>
          )}

          {canDelete && (
            <IconButton 
              onClick={handleDeleteAction}
              sx={{ color: 'error.main', bgcolor: (theme) => alpha(theme.palette.error.main, 0.1), '&:hover': { bgcolor: (theme) => alpha(theme.palette.error.main, 0.2) } }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          )}

          {/* MENU TRA CỨU: sổ cái / sổ kho / công nợ / lịch sử cập nhật / dữ liệu đã xoá / đổi mã */}
          <RecordLookupMenu item={item} apiCode={apiCode} changeCodeForm={changeCodeForm} onChanged={onRecordChanged} size="medium" />
        </Box>
      </Paper>
      <Box sx={{ p: { xs: 1, md: 2 }, pb: 10 }}>
        <DaiNhanDang
          anh={coverUrl ? <SecureImage src={coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
          chuCai={typeof tieuDe === 'string' ? tieuDe : ''}
          tieuDe={tieuDe}
          dongPhu={subTitleField && !isEmptyViewValue(item[subTitleField.field]) ? [asText(item[subTitleField.field])] : []}
          chips={chips}
          mauNhan={statusColor}
          benPhai={(item.date_created || item.date_updated) ? (
            <>
              <DongNgayTao nhan="Tạo lúc" ngay={item.date_created} nguoi={item.user_created} dinhDang={dinhDangNgay} />
              <DongNgayTao nhan="Cập nhật" ngay={item.date_updated} nguoi={item.user_updated} dinhDang={dinhDangNgay} />
            </>
          ) : null}
        />

        <HangSoLieu>
          {oSo.map((f, i) => (
            <OSoLieu key={f.field} nhan={f.label} giaTri={formatDisplayValue(item[f.field], f, item, context, false)} mau={MAU_O_SO[i % MAU_O_SO.length]} />
          ))}
        </HangSoLieu>

        {coverUrl && (
          <TheXem tieuDe="Hình ảnh">
            <Box sx={{ borderRadius: 1.5, overflow: 'hidden', display: 'flex', justifyContent: 'center', bgcolor: NEN_DANH_SACH }}>
              <SecureImage src={coverUrl} alt="Ảnh" style={{ maxWidth: '100%', maxHeight: 360, objectFit: 'contain' }} />
            </Box>
          </TheXem>
        )}

        {coThongTin && <TheXem tieuDe="Thông tin">{renderFieldGrid(thongTin, false)}</TheXem>}

        {tabs.length > 0 && (
          <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2, overflow: 'hidden' }}>
            <Tabs
              value={activeTabIndex}
              onChange={(e, v) => setTabIndex(v)}
              variant="scrollable" scrollButtons="auto"
              sx={sxThanhTabXem}
            >
              {tabs.map(t => <Tab key={t.key} label={t.title} />)}
            </Tabs>
            <Box sx={{ p: { xs: 1.5, md: 2 }, ...sxNoiDungTab }}>
              <ViewTabContent
                key={tabs[activeTabIndex].key}
                tab={tabs[activeTabIndex]} item={item} fields={fields} context={context}
                apiCode={apiCode} moduleCode={moduleCode} isReadOnlyFiles={readOnlyFiles}
                renderFieldGrid={renderFieldGrid}
              />
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
}